import { SEE_ITEM_COST, type ItemResponse } from '@palaccount/contracts';
import { Money } from '@palaccount/money';
import { useTranslation } from 'react-i18next';

import { Amount } from '@/components/amount';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';

/** One grid line as the margin reads it — the shape the three sales editors share. */
export interface MarginLine {
  itemId: string;
  /** '' = the item's stock unit. */
  unitId: string;
  qty: string;
  /** INV-L1: free units leave stock too, so they cost. '' = none. */
  bonusQty: string;
}

const DECIMAL_RE = /^\d+(\.\d+)?$/;

/**
 * Wave 4B — the document's margin, computed from what the screen already
 * holds: Σ (qty + bonus) × the unit's factor × the item's `costPrice`
 * (4dp, per stock unit) against the NET amount (subtotal after the document
 * discount, before tax). Pure, so the panel and its tests agree.
 *
 * A line with no item, no readable quantity or no cost price is LEFT OUT of
 * the cost — and says so in the note — rather than counted at zero, which
 * would overstate the margin by exactly the lines nobody costed.
 */
export function computeMargin(
  lines: readonly MarginLine[],
  items: ReadonlyMap<string, ItemResponse>,
  net: string,
  currency: string,
): { cost: string; profit: string; pct: string | null } | null {
  let cost = Money.zero(currency);
  let costed = false;
  for (const line of lines) {
    const item = line.itemId ? items.get(line.itemId) : undefined;
    if (item?.costPrice === undefined) continue;
    const qty = line.qty.trim();
    if (!DECIMAL_RE.test(qty)) continue;
    const bonus = DECIMAL_RE.test(line.bonusQty.trim()) ? line.bonusQty.trim() : '0';
    const factor =
      line.unitId === '' ? '1' : (item.units.find((unit) => unit.unitId === line.unitId)?.factor ?? '1');
    const units = Money.of(qty, currency).plus(Money.of(bonus, currency)).times(factor);
    cost = cost.plus(Money.of(item.costPrice, currency).times(units.toDecimalString()));
    costed = true;
  }
  if (!costed || !DECIMAL_RE.test(net.replace(/^-/, ''))) return null;
  const netMoney = Money.of(net, currency);
  const profit = netMoney.minus(cost.rounded());
  const pct = netMoney.isPositive()
    ? profit.times('100').dividedBy(netMoney.toDecimalString()).toFixed(2)
    : null;
  return { cost: cost.rounded().toFixed(2), profit: profit.toFixed(2), pct };
}

/** The net the margin is measured against: the subtotal less the document discount. */
export function netAmount(subtotal: string, discountAmount: string | null, currency: string): string | null {
  if (!DECIMAL_RE.test(subtotal)) return null;
  if (discountAmount === null || !DECIMAL_RE.test(discountAmount)) return subtotal;
  return Money.of(subtotal, currency).minus(Money.of(discountAmount, currency)).toFixed(2);
}

/**
 * The panel in the totals card. GATED on `inventory.see_item_cost`: without
 * it the item reads carry no `costPrice` at all (MD-03 §9 — "the field is
 * not rendered at all"), and this panel follows the same rule rather than
 * drawing a margin made of nothing.
 */
export function MarginPanel({
  lines,
  items,
  net,
  currency,
}: {
  lines: readonly MarginLine[];
  items: ReadonlyMap<string, ItemResponse>;
  /** Subtotal after the document discount, before tax; null while the totals are unreadable. */
  net: string | null;
  currency: string;
}) {
  const { t } = useTranslation();
  const canSeeCost = useCan(SEE_ITEM_COST);
  if (!canSeeCost || net === null) return null;
  const margin = computeMargin(lines, items, net, currency);
  if (margin === null) return null;
  const negative = margin.profit.startsWith('-');
  return (
    <section aria-label={t('sales.marginTitle')} className="grid gap-1.5 rounded-lg border border-dashed p-3 text-sm">
      <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
        {t('sales.marginTitle')}
      </div>
      <div className="flex items-center justify-between">
        <span>{t('sales.marginCost')}</span>
        <Amount value={margin.cost} currency={currency} />
      </div>
      <div className="flex items-center justify-between">
        <span>{t('sales.marginProfit')}</span>
        <Amount
          value={margin.profit}
          currency={currency}
          className={cn('font-semibold', negative && 'text-destructive')}
        />
      </div>
      {margin.pct !== null ? (
        <div className="flex items-center justify-between">
          <span>{t('sales.marginPct')}</span>
          <bdi dir="ltr" className={cn('font-mono tabular-nums', negative && 'text-destructive')}>
            {margin.pct}%
          </bdi>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('sales.marginNote')}</p>
    </section>
  );
}
